import { z } from "zod";
import { paymentRequirementSchema } from "./shared";
import { publicMemberTagsSchema } from "./memberTags";

// Shared JSON sub-schemas (used on Schedule address fields)
export const locationLinksSchema = z
  .object({
    yelp: z.string().optional(),
    google: z.string().optional(),
    instagram: z.string().optional(),
    facebook: z.string().optional(),
    map: z.string().optional(),
  })
  .catchall(z.unknown());

export const locationParkingSchema = z
  .object({
    // Nullable: GET /v1/locations/:id resolves parking via
    // resolveFirstVisitParking, which normalizes empty fields to null.
    link: z.string().nullable().optional(),
    description: z.string().nullable().optional(),
  })
  .catchall(z.unknown());

// Day-level business hours (derived from raw schedule hours JSON)
export const scheduleDayHoursSchema = z.object({
  day: z.number(),
  open: z.string().nullable(),
  close: z.string().nullable(),
  isOpen: z.boolean(),
});
export type ScheduleDayHours = z.infer<typeof scheduleDayHoursSchema>;

// Schedule-based public listing schema (replaces old locationSchema)
export const schedulePublicSchema = z.object({
  id: z.string(),
  salonId: z.string(),
  title: z.string(),
  slug: z.string().optional(),
  address: z.string().optional(),
  /** IANA timezone for this schedule/location (e.g. "America/New_York"). */
  timezone: z.string().optional(),
  lat: z.number().optional(),
  lng: z.number().optional(),
  /** Google Maps business listing ID; absent on older API versions. */
  googlePlaceId: z.string().nullable().optional(),
  images: z.array(z.string()).optional().default([]),
  /** URL (within `images`) tagged as the banner/hero photo. */
  bannerImage: z.string().optional(),
  /** URL (within `images`) tagged as the card/thumbnail photo. */
  cardImage: z.string().optional(),
  phone: z.string().optional(),
  links: locationLinksSchema.optional(),
  parking: locationParkingSchema.optional(),
  hours: z.array(scheduleDayHoursSchema).optional(),
  isActive: z.boolean().optional().default(true),
  createdAt: z.union([z.string(), z.date()]).optional(),
});

/** @deprecated Use schedulePublicSchema */
export const locationSchema = schedulePublicSchema;

export const schedulesListQuerySchema = z.object({
  salonId: z.string().min(1),
});
/** @deprecated Use schedulesListQuerySchema */
export const locationsListQuerySchema = schedulesListQuerySchema;

export const scheduleIdParamsSchema = z.object({
  scheduleId: z.string().min(1),
});
/** @deprecated Use scheduleIdParamsSchema */
export const locationIdParamsSchema = z.object({
  locationId: z.string().min(1),
});

export const locationDetailParamsSchema = locationIdParamsSchema;

export const memberUserParamsSchema = z.object({
  userId: z.string().min(1),
});
/** @deprecated Use memberUserParamsSchema */
export const locationUserParamsSchema = memberUserParamsSchema;

export const scheduleMemberVideoSchema = z
  .object({
    id: z.string().optional(),
    url: z.string().optional(),
    thumbnail: z.string().optional(),
  })
  .passthrough();
/** @deprecated Use scheduleMemberVideoSchema */
export const locationMemberVideoSchema = scheduleMemberVideoSchema;

export const scheduleMemberSchema = z.object({
  id: z.string(),
  userId: z.string().nullable().optional(),
  teamMemberId: z.string().nullable().optional(),
  username: z.string().nullable().optional(),
  name: z.string().nullable().optional(),
  photo: z.string().nullable().optional(),
  bio: z.string().nullable().optional(),
  phoneNumber: z.string().nullable().optional(),
  video: scheduleMemberVideoSchema.nullable().optional(),
});
/** @deprecated Use scheduleMemberSchema */
export const locationMemberSchema = scheduleMemberSchema;

export const scheduleServiceOptionSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().optional(),
  duration: z.number(),
  price: z.number(),
  referenceId: z.string().optional(),
});
/** @deprecated Use scheduleServiceOptionSchema */
export const locationServiceOptionSchema = scheduleServiceOptionSchema;

export const scheduleServiceSchema = z.object({
  id: z.string(),
  salonId: z.string().nullable().optional(),
  /** Set when this service is a per-team-member override. */
  teamMemberId: z.string().nullable().optional(),
  title: z.string(),
  description: z.string().optional(),
  price: z.number(),
  duration: z.number(),
  up: z.boolean(),
  hasPayAdvance: z.boolean(),
  hasConsultation: z.boolean(),
  paymentRequirement: paymentRequirementSchema.optional(),
  depositAmount: z.number().nullable().optional(),
  /**
   * Customers may book this service only at least this many minutes before
   * it starts; null means no notice. A member clone carries its business
   * service's value.
   */
  minBookingNoticeMinutes: z.number().int().positive().nullable().optional(),
  referenceId: z.string().optional(),
  categoryId: z.string().nullable().optional(),
  options: z.array(scheduleServiceOptionSchema).optional(),
});
/** @deprecated Use scheduleServiceSchema */
export const locationServiceSchema = scheduleServiceSchema;

export const schedulesListResponseSchema = z.object({
  items: z.array(schedulePublicSchema),
});
/** @deprecated Use schedulesListResponseSchema */
export const locationsListResponseSchema = schedulesListResponseSchema;

export const scheduleDetailResponseSchema = z.object({
  schedule: schedulePublicSchema,
  members: z.array(scheduleMemberSchema),
  services: z.array(scheduleServiceSchema),
});
/** @deprecated Use scheduleDetailResponseSchema */
export const locationDetailResponseSchema = z.object({
  location: schedulePublicSchema,
  members: z.array(scheduleMemberSchema),
  services: z.array(scheduleServiceSchema),
});

export const locationUserStatsSchema = z.object({
  /** Total completed services delivered by this member. */
  totalServices: z.number().optional().default(0),
  /** @deprecated Use totalServices. Retained for backwards compatibility. */
  totalHaircuts: z.number().optional().default(0),
});

export const locationUserReviewSchema = z.object({
  note: z.string(),
  /** Customer's first initial (e.g. "J."), or "Customer" when the name has no letter. */
  customerName: z.string().optional(),
  /** ISO timestamp of when the feedback was submitted. */
  submittedAt: z.string().optional(),
});

export const locationUserPhotoSchema = z.object({
  workPhotoId: z.string().optional(),
  viewCount: z.number().int().nonnegative().optional(),
  openCount: z.number().int().nonnegative().optional(),
  url: z.string(),
  thumbnail: z.string().optional(),
  type: z.enum(["image", "video"]).default("image"),
});

export const memberDetailsResponseSchema = z.object({
  stats: locationUserStatsSchema,
  reviews: z.array(locationUserReviewSchema).default([]),
  photos: z.array(locationUserPhotoSchema).default([]),
  bio: z.string().optional(),
  profilePhoto: z.string().optional(),
  profileVideo: scheduleMemberVideoSchema.optional(),
  tags: publicMemberTagsSchema.optional(),
});
/** @deprecated Use memberDetailsResponseSchema */
export const locationUserDetailsResponseSchema = memberDetailsResponseSchema;

// --- New canonical types ---
export type SchedulePublic = z.infer<typeof schedulePublicSchema>;
export type SchedulesListQuery = z.infer<typeof schedulesListQuerySchema>;
export type ScheduleIdParams = z.infer<typeof scheduleIdParamsSchema>;
export type ScheduleMember = z.infer<typeof scheduleMemberSchema>;
export type ScheduleServiceOption = z.infer<typeof scheduleServiceOptionSchema>;
export type ScheduleService = z.infer<typeof scheduleServiceSchema>;
export type SchedulesListResponse = z.infer<typeof schedulesListResponseSchema>;
export type ScheduleDetailResponse = z.infer<
  typeof scheduleDetailResponseSchema
>;
export type MemberDetailsResponse = z.infer<typeof memberDetailsResponseSchema>;
export type MemberUserParams = z.infer<typeof memberUserParamsSchema>;

// --- Legacy aliases (deprecated) ---
/** @deprecated Use SchedulePublic */
export type Location = z.infer<typeof schedulePublicSchema>;
/** @deprecated Use SchedulesListQuery */
export type LocationsListQuery = z.infer<typeof schedulesListQuerySchema>;
export type LocationIdParams = z.infer<typeof locationIdParamsSchema>;
export type LocationDetailParams = z.infer<typeof locationDetailParamsSchema>;
/** @deprecated Use MemberUserParams */
export type LocationUserParams = z.infer<typeof memberUserParamsSchema>;
/** @deprecated Use ScheduleMember */
export type LocationMember = z.infer<typeof scheduleMemberSchema>;
/** @deprecated Use ScheduleServiceOption */
export type LocationServiceOption = z.infer<typeof scheduleServiceOptionSchema>;
/** @deprecated Use ScheduleService */
export type LocationService = z.infer<typeof scheduleServiceSchema>;
/** @deprecated Use SchedulesListResponse */
export type LocationsListResponse = z.infer<typeof schedulesListResponseSchema>;
export type LocationDetailResponse = z.infer<
  typeof locationDetailResponseSchema
>;
export type LocationUserStats = z.infer<typeof locationUserStatsSchema>;
export type LocationUserReview = z.infer<typeof locationUserReviewSchema>;
export type LocationUserPhoto = z.infer<typeof locationUserPhotoSchema>;
/** @deprecated Use MemberDetailsResponse */
export type LocationUserDetailsResponse = z.infer<
  typeof memberDetailsResponseSchema
>;
